import React from 'react';
import { Button } from 'primereact/button';
import { SurveyEditor } from '../useSurveyEditor';
import { getEnabledTextQuestions, getRatingQuestion, roundLabel, SURVEY_RATING_MAX, SURVEY_TEXT_SLOTS } from '../surveyUtils';
import { SurveyQuestionCard } from './SurveyQuestionCard';
import { SurveyPreview } from './SurveyPreview';
import './SurveyEditorTab.scss';

interface Props {
  editor: SurveyEditor;
}

/** The locale of the translation in progress when it is for this slot. */
const translatingLocaleOf = (translating: string | null, slot: number): string | undefined =>
  translating?.startsWith(`${slot}-`) ? translating.slice(`${slot}-`.length) : undefined;

export function SurveyEditorTab({ editor }: Props): React.ReactElement {
  const { questions, locales, canWrite, activeLaunch, hasChanged, isSaving, problems } = editor;
  const textQuestions = getEnabledTextQuestions(questions);
  const rating = getRatingQuestion(questions);
  const canAddText = canWrite && textQuestions.length < SURVEY_TEXT_SLOTS.length;

  return (
    <div className='survey-editor'>
      <div className='survey-editor-main'>
        {activeLaunch && (
          <p className='survey-editor-note'>
            <i className='pi pi-info-circle' aria-hidden />
            {`${roundLabel(activeLaunch.number)} sigue con las preguntas con las que empezó. Lo que guardes aquí se usa en la próxima ronda.`}
          </p>
        )}
        {!canWrite && (
          <p className='survey-editor-note'>
            <i className='pi pi-lock' aria-hidden />
            No tienes permiso para editar la encuesta.
          </p>
        )}

        {textQuestions.map((question, index) => (
          <SurveyQuestionCard
            key={question.slot}
            question={question}
            position={index + 1}
            locales={locales}
            canWrite={canWrite}
            onTextChange={(locale, text) => editor.setQuestionText(question.slot, locale, text)}
            onRequiredChange={(required) => editor.setQuestionRequired(question.slot, required)}
            onRemove={() => editor.removeQuestion(question.slot)}
            onTranslate={(locale) => editor.translateQuestion(question.slot, locale)}
            translatingLocale={translatingLocaleOf(editor.translating, question.slot)}
          />
        ))}

        {canAddText && (
          <button type='button' className='survey-editor-add' onClick={editor.addQuestion}>
            <i className='pi pi-plus' aria-hidden />
            <span>Añadir pregunta</span>
            <small>{`${textQuestions.length} de ${SURVEY_TEXT_SLOTS.length}`}</small>
          </button>
        )}

        {rating && (
          <SurveyQuestionCard
            question={rating}
            position={textQuestions.length + 1}
            locales={locales}
            canWrite={canWrite}
            onTextChange={(locale, text) => editor.setQuestionText(rating.slot, locale, text)}
            onRequiredChange={(required) => editor.setQuestionRequired(rating.slot, required)}
            onRemove={() => editor.setRating(false)}
            onTranslate={(locale) => editor.translateQuestion(rating.slot, locale)}
            translatingLocale={translatingLocaleOf(editor.translating, rating.slot)}
          />
        )}
        {!rating && canWrite && (
          <button type='button' className='survey-editor-add' onClick={() => editor.setRating(true)}>
            <i className='pi pi-star' aria-hidden />
            <span>{`Añadir valoración del 1 al ${SURVEY_RATING_MAX}`}</span>
            <small>Siempre al final</small>
          </button>
        )}

        {canWrite && hasChanged && (
          <div className='survey-editor-savebar' role='region' aria-label='Cambios sin guardar'>
            <div className='survey-editor-savebar-text'>
              <strong>Cambios sin guardar</strong>
              {problems.length > 0 && (
                <ul>
                  {problems.map((problem) => (
                    <li key={problem}>{problem}</li>
                  ))}
                </ul>
              )}
            </div>
            <div className='survey-editor-savebar-actions'>
              <Button type='button' label='Descartar' text severity='secondary' disabled={isSaving} onClick={editor.discard} />
              <Button
                id='survey-save-button'
                type='button'
                label='Guardar cambios'
                icon='pi pi-check'
                loading={isSaving}
                disabled={isSaving || problems.length > 0}
                onClick={editor.save}
              />
            </div>
          </div>
        )}
      </div>

      <SurveyPreview questions={questions} locale={locales[0]} />
    </div>
  );
}
